import { browser } from "$app/environment";
import { concat, defer, delay, distinctUntilChanged, EMPTY, filter, fromEvent, map, merge, mergeWith, Observable, of, shareReplay, startWith, takeUntil, tap } from "rxjs";

/**
 * Observe the browser online state.
 * @returns `true` when online and `false` when offline. An EMPTY observable Server-Side.
 */
 export function obsOnline() {
	if (!browser) return EMPTY as Observable<boolean>;
	const off$ = fromEvent(window, 'offline').pipe(map(() => false));
	const on$ = fromEvent(window, 'online').pipe(map(() => true));
	return merge(on$, off$).pipe(mergeWith(defer(() => of(window.navigator.onLine))));
}

/**
 * An observable that can be used to create online-safe streams.
 * @returns A stream that will complete after either emitting only a `true` OR a `false`
 * followed by a `true`.
 */
export function obsUntilOnline() {
	const online$ = obsOnline().pipe(distinctUntilChanged(), shareReplay(1));
	const isOnline$ = online$.pipe(filter(online => online), delay(0));
	return online$.pipe(takeUntil(isOnline$)); // only `true` or `false` followed by `true`
}


/**
 * Observe the window/tab focus/blur state.
 * @returns `true` when in foreground (focused) and `false` when in the background (blured). An EMPTY observable Server-Side.
 */
export function obsFocus() {
	if (!browser) return EMPTY as Observable<boolean>;
	const off$ = fromEvent(window, 'blur').pipe(map(() => false));
	const on$ = fromEvent(window, 'focus').pipe(map(() => true));
	return defer(() => of(document.hasFocus())).pipe(mergeWith(on$, off$));
}

export const windowFocus$ = obsFocus().pipe(
	filter(v => v),
	tap(() => console.log('Window focused.')),
	shareReplay(1)
);
